import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { entity, query, notNull } from '~/prix'
import AppLayout from '~/components/appLayout'
import Handle from '~/prix/react/components/handle'
import MenuItemCard from '~/prix/react/components/menu/menuItemCard'
import { alertColors, alertLabels, AlertLevel, timeSeriesInfo } from './timeSeries.data'
import TimeSeries from './timeSeries.component'
import Badge from '~/prix/react/components/badge'
import useItems from '~/prix/react/hooks/items'
import useAPI from '~/prix/react/hooks/api'

export default function TimeSeriesScreen() {
  const { context } = useAPI()
  const params = useParams()
  const [menuVisibility, setMenuVisibility] = React.useState(true)

  const timeSeriesResult = useItems(
    () =>
      query('timeSeries')
        .select({
          key: entity('timeSeries').property('key'),
          alertLevel: entity('timeSeries').property('mostRecentAlert'),
          date: entity('timeSeries').property('date'),
        })
        .where(notNull(entity('timeSeries').property('components'))),
    [],
  )

  const hasTriedFetch = React.useRef(false)
  React.useEffect(() => {
    if (!timeSeriesResult.isLoading) {
      hasTriedFetch.current = true
    }
  }, [timeSeriesResult.isLoading])
  const errorToShow = hasTriedFetch.current ? timeSeriesResult.error : undefined

  const timeSeries = useMemo(() => {
    if (!timeSeriesResult.items) return []

    const isAppSebraeManager = context?.user?.roles?.includes('appSebraeManager')

    const filteredItems = timeSeriesResult.items.filter(item => {
      if (isAppSebraeManager) {
        return item.key === 'legalEntityAttendanceAppSebrae'
      }

      return timeSeriesInfo[item.key as keyof typeof timeSeriesInfo]
    })

    const uniqueItems = filteredItems.reduce((acc: typeof filteredItems, curr) => {
      if (curr.key === 'legalEntityIndividualPortfolioCourses') {
        if (acc.some(item => item.key === 'legalEntityIndividualPortfolioCourses')) {
          return acc
        }
      }
      acc.push(curr)
      return acc
    }, [])

    return uniqueItems
      .map(item => {
        const typedItem = item as {
          key: string
          alertLevel: AlertLevel
          date: string
          order: number
        }
        return {
          ...typedItem,
          ...timeSeriesInfo[typedItem.key as keyof typeof timeSeriesInfo],
        }
      })
      .sort((a, b) => a.order - b.order)
  }, [timeSeriesResult.items, context])

  const selectedTimeSeries = useMemo(
    () => timeSeries.find(timeSeries => timeSeries.key === params.timeSeriesKey) ?? null,
    [timeSeriesResult.items, params.timeSeriesKey],
  )

  return (
    <AppLayout
      title='Séries temporais'
      dockActive='analytical-library'
      initialMenuVisibility={menuVisibility}
      menu={
        <Handle error={errorToShow} isLoading={timeSeriesResult.isLoading}>
          {timeSeries.map(current => (
            <MenuItemCard
              key={current.key}
              timeSeriesKey={current.key}
              href={`/app/time-series/${current.key}`}
              title={current.title}
              label={current.label}
              isActive={current.key === params.timeSeriesKey}
              childrenOnRight={
                // For Now
                current.alertLevel && current.alertLevel !== 'informative' ? (
                  <Badge background={alertColors[current.alertLevel as keyof typeof alertColors]}>
                    {alertLabels[current.alertLevel as keyof typeof alertColors]}
                  </Badge>
                ) : null
              }
              menuVisibility={setMenuVisibility}
            />
          ))}
        </Handle>
      }
    >
      {selectedTimeSeries && (
        <TimeSeries
          timeSeries={{
            ...selectedTimeSeries,
          }}
        />
      )}
    </AppLayout>
  )
}
